import classNames from 'classnames';
import { useMemo } from 'react';
import { memo, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown, DropdownToggle } from 'shards-react';
import AllowedReferences from './components/AllowedReferences';
import ManualReference from './components/ManualReference';
import RenderReferences from './components/RenderReferences';
import RecognisedReferences from './components/RecognisedReferences';

const ReferenceNew = ({
    serviceId,
    approved,
    isManual,
    withCategory,
    savedReferences = [],
    recognisedRef = [],
}) => {
    const [isOpen, setIsOpen] = useState(() => false);
    const toggleDropdown = useCallback(
        () => setIsOpen((prevState) => !prevState),
        []
    );
    const [references, setReferences] = useState(() => savedReferences);
    const [recRef, setRecRef] = useState(recognisedRef);
    const [singleRefTooltip, setSingleRefTooltip] = useState(false);

    const removeReference = useCallback((value) => {
        const referenceToRemove = value?._id ?? value;
        return setReferences((prevState) =>
            prevState.filter((item) => {
                const selectedElement = item?._id ?? item;
                return selectedElement !== referenceToRemove;
            })
        );
    }, []);

    const formData = useMemo(() => {
        if (isManual) return references;
        if (references?.length === 0) return references;
        return references.map(({ _id }) => _id);
    }, [references, isManual]);

    const dropDownTheme = classNames(
        recognisedRef.length ? 'warning' : references.length ? 'success' : 'danger'
    );
    const addReference = useCallback((newReference) => {
        setReferences((prevState) => [...prevState, newReference]);
        setIsOpen(true);
    }, []);

    const recRefData = useMemo(() => {
        if (isManual) return [];
        if (recRef?.length === 0) return recRef;
        return recRef.map(({ _id }) => _id);
    }, [recRef, isManual]);

    return (
        <div className='d-flex justify-content-evenly align-items-center w-100'>
            {isManual && (
                <ManualReference
                    approved={approved}
                    references={references}
                    addReference={addReference}
                />
            )}
            {!isManual && (
                <AllowedReferences
                    approved={approved}
                    withCategory={withCategory}
                    references={references}
                    recRef={recRef}
                    service={serviceId}
                    addReference={addReference}
                />
            )}
            <Dropdown open={isOpen} toggle={toggleDropdown}>
                {/* Pack all references in submit form! */}
                <input
                    id='reference'
                    form='detect-types-form'
                    type='text'
                    required
                    hidden
                    readOnly
                    value={formData}
                />
                {/* And also the recognised references */}
                <input
                    id='recognisedRef'
                    form='detect-types-form'
                    type='text'
                    required
                    hidden
                    readOnly
                    value={recRefData}
                />
                {/* END COMMENT */}
                {(references?.length > 1 || recRef?.length > 0) && (
                    <DropdownToggle
                        theme={dropDownTheme}
                        style={{
                            minWidth: '110px',
                            color: 'white',
                            marginLeft: '1rem',
                        }}
                        pill
                    >
                        Referenzen
                        <i
                            data-test='refs-dropdown-btn'
                            className='fas fa-caret-down ml-2'
                            style={{
                                transform: isOpen && 'rotate(180deg)',
                                transitionDuration: '0.3s',
                                transitionTimingFunction: 'ease-in-out',
                            }}
                        />
                    </DropdownToggle>
                )}
                {(references?.length > 1 || recRef?.length > 0) && (
                    <div
                        style={{
                            zIndex: 1030,
                            minWidth: '31rem',
                            transform: !isOpen && 'scaleY(0)',
                            transitionDuration: '0.3s',
                            transitionTimingFunction: 'ease-in-out',
                            transformOrigin: 'top',
                        }}
                        className={`position-absolute d-flex flex-wrap shadow-lg bg-light rounded ml-3 mt-2 border ${
                            recognisedRef.length
                                ? 'border-warning'
                                : 'border-success'
                        }`}
                    >
                        <RenderReferences
                            references={references}
                            withCategory={withCategory}
                            removeReference={removeReference}
                            approved={approved}
                        />
                        {recRef.length > 0 && (
                            <RecognisedReferences
                                recRef={recRef}
                                setRecRef={setRecRef}
                                addReference={addReference}
                            />
                        )}
                    </div>
                )}
                {references?.length === 1 && recRef?.length === 0 && (
                    <div
                        data-test='single-chosenref'
                        style={{
                            minWidth: 'max-content',
                            zIndex: 10,
                            cursor: 'pointer',
                        }}
                        className='position-relative d-inline-block mx-3'
                        onClick={() => !approved && removeReference(references[0])}
                        onMouseOver={() => setSingleRefTooltip(true)}
                        onMouseLeave={() => setSingleRefTooltip(false)}
                        title={
                            approved
                                ? `Referenz: ${references[0]?._id ?? references[0]}`
                                : 'Zum Entfernen anklicken!'
                        }
                    >
                        {!approved && singleRefTooltip && (
                            <div
                                style={{ right: -32, top: -12, zIndex: -1 }}
                                className='position-absolute btn'
                            >
                                <i
                                    className='fa fa-minus-circle fa-lg complement-color'
                                    aria-hidden='true'
                                />
                            </div>
                        )}
                        <div className='btn btn-outline-success rounded-pill'>
                            <p className='m-0'>
                                {references[0]?.reference ?? references[0]}{' '}
                                <span>
                                    {withCategory && ` - ${references[0]?.category}`}
                                </span>
                            </p>
                        </div>
                    </div>
                )}
            </Dropdown>
        </div>
    );
};

/**
 * Take reference and approved value from redux and return it in components props.
 */
const mapStateToProps = ({
    savedDocument: { approved, reference, recognisedRef },
    userData: { services },
}) => {
    const serviceId = window.location.pathname.split('/')[2];
    const [serviceData] = services.filter(({ id }) => id === serviceId);
    const isManual = !!serviceData?.mainModule['-manualReferenceInput'];
    const withCategory = !!serviceData?.mainModule['-withCategory'];

    reference = Array.isArray(reference) ? reference : reference?.split(',');

    return {
        approved,
        savedReferences: reference,
        serviceId,
        isManual,
        withCategory,
        recognisedRef,
    };
};

export default connect(mapStateToProps)(memo(ReferenceNew)); //Export component and connect it with redux.
